import React, { FC } from 'react';
import { logEventAsArray } from '@/analytics';
import { Button, Spinner } from '@mofid-securities/uikit';
import { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { SelectButton } from '@/components/Button';
import {
  IUseInteractiveNavCharOptionsProps,
  useDisabledNotCompletedPeriods,
  UseDisabledNotCompletedPeriodsProps,
  useInteractiveNavCharOptions,
} from './InteractiveNavChart.hooks';
import { getPeriodTitle } from './InteractiveNavChart.utils';
import {
  TNavChartAnalytics,
  TNavChartPeriod,
  TNavChartPeriods,
} from './InteractiveNavChart.types';

export interface IInteractiveNavChartProps
  extends Pick<
      IUseInteractiveNavCharOptionsProps,
      'series' | 'renderTooltip' | 'fillColor'
    >,
    Pick<UseDisabledNotCompletedPeriodsProps, 'startDate'> {
  //
  isLoading?: boolean;
  isEmpty?: boolean;
  error?: Error | null | any;
  onErrorRetry?: Function;
  //
  periods?: TNavChartPeriods;
  selectedPeriod?: TNavChartPeriod;
  onPeriodChange?: (__period: TNavChartPeriod) => void;
  //
  analytics?: TNavChartAnalytics;
  yAxisTitle?: string;
  xAxis?: Options['xAxis'];
}

export const InteractiveNavChart: FC<IInteractiveNavChartProps> = ({
  series,
  renderTooltip,
  fillColor = '#80d3cb2e',
  //
  startDate,
  //
  isLoading,
  isEmpty,
  error,
  onErrorRetry,
  //
  periods: rawPeriods,
  selectedPeriod,
  onPeriodChange,
  //
  analytics,
  yAxisTitle = 'ریال',
  xAxis,
}) => {
  const options = useInteractiveNavCharOptions({
    renderTooltip,
    fillColor,
    series,
    yAxisTitle,
    xAxis,
  });

  const periods = useDisabledNotCompletedPeriods({
    periods: rawPeriods,
    startDate,
  });

  return (
    <div>
      <div className="flex flex-col">
        <>
          {isLoading ? (
            <div className="flex min-h-[250px] flex-col items-center justify-center text-sm text-gray-80">
              <Spinner className="mb-2" />
              درحال بارگذاری
            </div>
          ) : error ? (
            <div className="flex  min-h-[250px] flex-col items-center justify-center">
              <span className="w-3/4 text-center text-sm font-medium text-error">
                {error?.message || error}
              </span>
              {typeof onErrorRetry === 'function' && (
                <Button
                  className="mt-7 w-[160px] text-sm font-medium"
                  size="md"
                  onClick={() => onErrorRetry()}
                >
                  تلاش مجدد
                </Button>
              )}
            </div>
          ) : isEmpty ? (
            <div className="flex min-h-[250px] flex-col items-center justify-center text-sm text-gray-80">
              موردی برای نمایش در بازه انتخابی موجود نمیباشد.
            </div>
          ) : (
            <div
              className="min-h-[250px]"
              id={analytics?.chart?.id}
              onClick={() => logEventAsArray(analytics?.chart?.analytics)}
              onTouchStartCapture={() =>
                logEventAsArray(analytics?.chart?.analytics)
              }
            >
              <HighchartsReact
                constructorType={'stockChart'}
                highcharts={Highcharts}
                options={options}
              />
            </div>
          )}
        </>
      </div>
      {!!periods && periods?.length > 0 && (
        <div className="mt-5 flex flex-row-reverse gap-2 overflow-x-auto scroll-hide">
          {periods?.map?.((item) => (
            <SelectButton
              key={item.month}
              analytics={analytics?.period?.analytics}
              className="min-w-[56px]"
              disabled={item.disabled}
              id={analytics?.period?.id}
              isSelected={selectedPeriod?.month === item?.month}
              value={item.month}
              onClick={() => {
                if (typeof onPeriodChange === 'function') {
                  const period = periods.find(
                    ({ month }) => month === item?.month
                  );
                  onPeriodChange(period!);
                }
              }}
            >
              {getPeriodTitle(item)}
            </SelectButton>
          ))}
        </div>
      )}
    </div>
  );
};
